// src/components/Icons.tsx
//
// Small, dependency-free line-icon set (Lucide-style, 24×24, currentColor
// stroke) used across the coach-facing surfaces. Inline SVG keeps the bundle
// lean and lets each icon inherit color/size from its context — a more
// professional look than emoji, and crisp at any DPI.

import React from "react";

export interface IconProps {
  size?: number;
  color?: string;
  strokeWidth?: number;
  style?: React.CSSProperties;
}

const base = (
  size: number,
  color: string,
  strokeWidth: number,
  style: React.CSSProperties | undefined,
  children: React.ReactNode
) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke={color}
    strokeWidth={strokeWidth}
    strokeLinecap="round"
    strokeLinejoin="round"
    style={{ flexShrink: 0, display: "block", ...style }}
    aria-hidden="true"
    focusable="false"
  >
    {children}
  </svg>
);

export const PencilIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
    </>
  ));

export const LockIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </>
  ));

export const ClockIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </>
  ));

export const RefreshIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <polyline points="23 4 23 10 17 10" />
      <polyline points="1 20 1 14 7 14" />
      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
    </>
  ));

export const InfoIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <circle cx="12" cy="12" r="10" />
      <line x1="12" y1="11" x2="12" y2="16" />
      <line x1="12" y1="8" x2="12.01" y2="8" />
    </>
  ));

export const AlertTriangleIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </>
  ));

export const HistoryIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <path d="M3 3v5h5" />
      <path d="M3.05 13A9 9 0 1 0 6 5.3L3 8" />
      <polyline points="12 7 12 12 15 14" />
    </>
  ));

export const UsersIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </>
  ));

export const ScaleIcon = ({ size = 16, color = "currentColor", strokeWidth = 1.9, style }: IconProps) =>
  base(size, color, strokeWidth, style, (
    <>
      <path d="M12 3v18" />
      <path d="M5 7h14" />
      <path d="m5 7-3 6a3 3 0 0 0 6 0Z" />
      <path d="m19 7-3 6a3 3 0 0 0 6 0Z" />
      <path d="M7 21h10" />
    </>
  ));

export const ChevronDownIcon = ({ size = 16, color = "currentColor", strokeWidth = 2, style }: IconProps) =>
  base(size, color, strokeWidth, style, <polyline points="6 9 12 15 18 9" />);
